import type { FormField } from "@responsescope/shared";

interface FieldRendererProps {
  field: FormField;
  value: string;
  files: File[];
  onChange: (value: string) => void;
  onFilesChange: (files: File[]) => void;
}

// One switch per field type keeps adding a new type a matter of adding a
// case here plus the type in packages/shared/src/form.ts — no other file
// needs to change.
export function FieldRenderer({ field, value, files, onChange, onFilesChange }: FieldRendererProps) {
  switch (field.fieldType) {
    case "text":
      return (
        <input
          className="rs-input"
          type="text"
          placeholder={field.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "textarea":
      return (
        <textarea
          className="rs-input rs-textarea"
          placeholder={field.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
        />
      );

    case "number":
      return (
        <input
          className="rs-input"
          type="number"
          placeholder={field.placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "date":
      return <input className="rs-input" type="date" value={value} onChange={(e) => onChange(e.target.value)} />;

    case "time":
      return <input className="rs-input" type="time" value={value} onChange={(e) => onChange(e.target.value)} />;

    case "select":
      return (
        <select className="rs-input" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="" disabled>
            選択してください
          </option>
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case "radio":
      return (
        <div className="rs-radio-group">
          {field.options.map((opt) => (
            <label key={opt.value} className="rs-radio-option">
              <input
                type="radio"
                name={field.fieldKey}
                value={opt.value}
                checked={value === opt.value}
                onChange={(e) => onChange(e.target.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      );

    case "checkbox":
      return (
        <label className="rs-checkbox-option">
          <input type="checkbox" checked={value === "true"} onChange={(e) => onChange(e.target.checked ? "true" : "false")} />
          {field.label}
        </label>
      );

    case "multiselect": {
      const selected = value ? value.split(",") : [];
      const toggle = (optValue: string) => {
        const next = selected.includes(optValue) ? selected.filter((v) => v !== optValue) : [...selected, optValue];
        onChange(next.join(","));
      };
      return (
        <div className="rs-checkbox-group">
          {field.options.map((opt) => (
            <label key={opt.value} className="rs-checkbox-option">
              <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
              {opt.label}
            </label>
          ))}
        </div>
      );
    }

    case "image":
    case "file": {
      const accept = field.fieldType === "image" ? "image/*" : undefined;
      return (
        <div className="rs-file-field">
          <input
            className="rs-input"
            type="file"
            accept={accept}
            multiple={(field.maxFiles ?? 1) > 1}
            onChange={(e) => onFilesChange(Array.from(e.target.files ?? []))}
          />
          {files.length > 0 && (
            <ul className="rs-file-list">
              {files.map((f) => (
                <li key={f.name}>
                  {f.name} ({Math.round(f.size / 1024)}KB)
                  <button
                    type="button"
                    className="rs-file-remove"
                    onClick={() => onFilesChange(files.filter((x) => x !== f))}
                  >
                    削除
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    default:
      return null;
  }
}
